/* Presentation only: every opening uses the supplied, already assigned card. */
(() => {
  if (customElements.get('piranha-pack-opening')) return;
  const smooth = 'cubic-bezier(.16,1,.3,1)';
  const total = 5200;
  const warmed = new Map();
  const AudioContext = globalThis.AudioContext || globalThis.webkitAudioContext;
  const soundPreference = 'piranha-opening-muted';
  let context, activeSound, soundBuffers, muted = false;
  try { muted = localStorage.getItem(soundPreference) === 'true'; } catch { /* Private browsing can disable storage. */ }
  const soundControls = new Set();
  const unlockSound = () => {
    if (!AudioContext) return Promise.resolve(null);
    try {
      context ||= new AudioContext();
      // Called directly by the opening tap, before artwork decoding or other awaits.
      return Promise.resolve(context.resume()).then(() => context.state === 'running' ? context : null).catch(() => null);
    } catch { return Promise.resolve(null); }
  };
  const toggleSound = () => {
    muted = !muted;
    try { localStorage.setItem(soundPreference, String(muted)); } catch { /* The in-page preference still works. */ }
    activeSound?.volume();
    soundControls.forEach(update => update());
  };

  // An original, lightweight soundtrack. No audio downloads or third-party service.
  function soundtrack(audio) {
    activeSound?.stop();
    const nodes = [], sources = [], start = audio.currentTime;
    const master = audio.createGain(); master.gain.value = muted ? 0 : .42;
    const fade = audio.createGain(), compressor = audio.createDynamicsCompressor();
    compressor.threshold.value = -16; compressor.knee.value = 16; compressor.ratio.value = 3;
    compressor.attack.value = .004; compressor.release.value = .18;
    fade.gain.setValueAtTime(1, start + 4.65); fade.gain.linearRampToValueAtTime(0, start + 5.16);
    master.connect(fade); fade.connect(compressor); compressor.connect(audio.destination);
    nodes.push(master, fade, compressor);
    let stopped = false;
    const sound = {
      volume() {
        if (stopped) return;
        master.gain.cancelScheduledValues(audio.currentTime);
        master.gain.setTargetAtTime(muted ? 0 : .42, audio.currentTime, .015);
      },
      stop() {
        if (stopped) return;
        stopped = true;
        sources.forEach(node => { try { node.stop(); } catch { /* Already ended. */ } });
        nodes.forEach(node => node.disconnect());
        if (activeSound === sound) activeSound = null;
      }
    };
    activeSound = sound;
    try {
      if (!soundBuffers) {
        const noise = audio.createBuffer(1, audio.sampleRate, audio.sampleRate);
        const impulse = audio.createBuffer(2, Math.ceil(audio.sampleRate * 1.45), audio.sampleRate);
        let seed = 173;
        const random = () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 2147483648 - 1; };
        const data = noise.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = random();
        for (let channel = 0; channel < 2; channel++) {
          const tail = impulse.getChannelData(channel);
          let softened = 0;
          for (let i = 0; i < tail.length; i++) {
            softened = softened * .42 + random() * .58;
            tail[i] = i < audio.sampleRate * .018 ? 0 : softened * Math.pow(1 - i / tail.length, 3.4);
          }
        }
        soundBuffers = { noise, impulse };
      }
      const room = audio.createConvolver(), wet = audio.createGain();
      room.buffer = soundBuffers.impulse; wet.gain.value = .28;
      room.connect(wet); wet.connect(master); nodes.push(room, wet);
      const envelope = (node, time, duration, level, attack, ambience = false) => {
        const gain = audio.createGain(); nodes.push(gain); node.connect(gain); gain.connect(master);
        if (ambience) gain.connect(room);
        gain.gain.setValueAtTime(0, time);
        gain.gain.linearRampToValueAtTime(level, time + attack);
        gain.gain.exponentialRampToValueAtTime(.0001, time + duration);
      };
      const air = (offset, duration, level, from, to, crunchy = false, pan = 0) => {
        const time = start + offset, source = audio.createBufferSource(), filter = audio.createBiquadFilter();
        const stereo = audio.createStereoPanner(); stereo.pan.setValueAtTime(pan, time); stereo.pan.linearRampToValueAtTime(-pan, time + duration);
        source.buffer = soundBuffers.noise; source.loop = true; filter.type = 'bandpass'; filter.Q.value = crunchy ? 2.2 : .8;
        filter.frequency.setValueAtTime(from, time); filter.frequency.exponentialRampToValueAtTime(to, time + duration);
        sources.push(source); nodes.push(source, filter, stereo); source.connect(filter); filter.connect(stereo);
        envelope(stereo, time, duration, level, crunchy ? .006 : duration * .56, !crunchy);
        source.start(time); source.stop(time + duration);
      };
      const tone = (offset, duration, frequency, level, end = frequency, type = 'sine', attack = .012) => {
        const time = start + offset, oscillator = audio.createOscillator(); oscillator.type = type;
        oscillator.frequency.setValueAtTime(frequency, time);
        oscillator.frequency.exponentialRampToValueAtTime(end, time + duration);
        sources.push(oscillator); nodes.push(oscillator);
        envelope(oscillator, time, duration, level, attack, true); oscillator.start(time); oscillator.stop(time + duration);
      };
      // Tight tactile crackle, with a heavier tear where the foil separates.
      air(.18, .2, .13, 4200, 2300, true);
      for (let i = 0; i < 10; i++) air(.46 + i * .036, .085 + i * .009, .32 - i * .014, 2600 + (i % 3) * 950, 1050, true, .12);
      air(.5, .7, .46, 3400, 380, false, -.35);
      tone(.5, .24, 160, .12, 65, 'triangle');
      air(.89, .65, .2, 1300, 340, false, .4);
      // A rising harmonic bed joins the spins into one continuous build.
      tone(1.35, 1.82, 146.83, .11, 293.66, 'triangle', 1.15);
      tone(1.45, 1.65, 220, .045, 440, 'sine', 1.05);
      air(1.46, .79, .48, 420, 2300, false, -.7);
      air(2.04, .81, .52, 650, 3300, false, .7);
      air(2.75, .56, .4, 1100, 6000, false, -.4);
      // A brief breath before the bass impact, wide air bloom and glass harmonics.
      tone(3.3, .74, 117.46, .34, 46.25, 'sine');
      tone(3.3, .6, 146.83, .15, 73.42, 'triangle');
      air(3.3, .95, .4, 2600, 320, false, .25);
      [293.66, 440, 587.33, 739.99, 880, 1174.66].forEach((note, i) => {
        tone(3.32 + i * .038, 1.28, note, .11 - i * .01);
        tone(3.32 + i * .038, .5, note * 2.003, .012);
      });
      [1174.66, 1479.98, 1760, 2349.32].forEach((note, i) => tone(3.77 + i * .095, .65, note, .024 - i * .004));
      return sound;
    } catch { sound.stop(); return null; }
  }

  class PiranhaPackOpening extends HTMLElement {
    static warm(urls) {
      for (const src of new Set(urls.filter(Boolean))) {
        if (warmed.has(src)) continue;
        const image = new Image(); image.decoding = 'async'; image.src = src;
        const promise = image.decode().catch(() => { warmed.delete(src); });
        warmed.set(src, { image, promise });
        if (warmed.size > 24) warmed.delete(warmed.keys().next().value);
      }
    }

    connectedCallback() {
      if (this.ready) return;
      this.ready = true; this.animations = []; this.generation = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotion = () => { if (this.motion.matches && this.pending) this.finish(); };
      this.motion.addEventListener('change', this.onMotion);
      this.onTrigger = () => this.play().catch(() => {});
      this.querySelector('[data-opening-trigger]')?.addEventListener('click', this.onTrigger);
      this.soundButton = this.querySelector('[data-opening-sound]');
      this.updateSound = () => {
        if (!this.soundButton) return;
        this.soundButton.hidden = !AudioContext;
        this.soundButton.setAttribute('aria-pressed', String(muted));
        this.soundButton.title = muted ? 'Turn sound on' : 'Mute sound';
        const label = this.soundButton.querySelector('[data-sound-label]');
        if (label) label.textContent = muted ? 'Sound off' : 'Sound on';
      };
      this.onSound = event => { event.stopPropagation(); toggleSound(); };
      this.soundButton?.addEventListener('click', this.onSound);
      soundControls.add(this.updateSound); this.updateSound();
      this.onHidden = () => { if (document.hidden) this.sound?.stop(); };
      document.addEventListener('visibilitychange', this.onHidden);
      // Start decoding when the scene appears, not when the collector taps it.
      this.prepare().catch(() => {});
    }

    disconnectedCallback() {
      if (this.pending) this.finish();
      this.motion?.removeEventListener('change', this.onMotion);
      this.querySelector('[data-opening-trigger]')?.removeEventListener('click', this.onTrigger);
      this.sound?.stop();
      this.soundButton?.removeEventListener('click', this.onSound);
      soundControls.delete(this.updateSound);
      document.removeEventListener('visibilitychange', this.onHidden);
      this.ready = false;
    }

    prepare() {
      // Decorative brand marks must not delay or block the actual opening.
      const images = [...this.querySelectorAll('img')].filter(image => !image.closest?.('.pack-opening-back'));
      const signature = images.map(image => image.currentSrc || image.src || '').join('|');
      if (this.prepared && this.preparedSignature === signature) return this.prepared;
      this.preparedSignature = signature;
      this.prepared = Promise.all(images.map(async image => {
        image.loading = 'eager'; image.decoding = 'async';
        if (image.decode) await image.decode();
        if (!image.complete || !image.naturalWidth) throw new Error('Opening image unavailable');
      })).then(() => { this.fitCardFaces(); }).catch(error => { this.prepared = null; throw error; });
      return this.prepared;
    }

    emit(name) { this.dispatchEvent(new CustomEvent(`pack-opening:${name}`, { bubbles: true })); }

    play() {
      if (this.pending) return this.pending;
      if (this.dataset.state === 'complete') return Promise.resolve();
      this.soundReady = this.motion.matches ? Promise.resolve(null) : unlockSound();
      const generation = ++this.generation;
      this.initialPose = getComputedStyle(this.querySelector('.pack-opening-wrapper')).transform;
      this.dataset.state = 'preparing';
      this.querySelector('[data-opening-trigger]')?.setAttribute('disabled', '');
      this.pending = new Promise((resolve, reject) => { this.resolve = resolve; this.reject = reject; });
      const pending = this.pending;
      this.emit('start');
      this.prepare().then(() => {
        if (generation !== this.generation || !this.pending) return;
        if (this.motion.matches || !this.animate) this.finish();
        else this.animateOpening();
      }).catch(() => {
        if (generation !== this.generation || !this.pending) return;
        const reject = this.reject;
        this.pending = null; this.resolve = null; this.reject = null;
        this.dataset.state = 'sealed';
        this.sound?.stop();
        this.querySelector('[data-opening-trigger]')?.removeAttribute('disabled');
        this.emit('error'); reject(new Error('Opening image unavailable'));
      });
      return pending;
    }

    fitCardFaces() {
      if (this.dataset.faceKind !== 'slab') return;
      const image = this.querySelector('.pack-opening-result > img');
      if (!image?.naturalWidth || !image.naturalHeight) return;
      this.style.setProperty('--card-width', `calc(var(--card-height) * ${image.naturalWidth / image.naturalHeight})`);
    }

    animateOpening() {
      this.dataset.state = 'opening';
      const highlight = this.dataset.celebration === 'highlight';
      const generation = this.generation, soundStart = performance.now();
      this.soundReady.then(audio => {
        // Never delay the animation for audio, or play a late soundtrack after closing.
        if (audio && generation === this.generation && this.pending && !document.hidden && performance.now() - soundStart < 150) this.sound = soundtrack(audio);
      });
      const start = document.timeline.currentTime;
      const run = (selector, frames, duration, delay = 0, easing = smooth) => {
        const targets = selector ? this.querySelectorAll(selector) : [this];
        return [...targets].map((element, index) => {
          const animation = element.animate(typeof frames === 'function' ? frames(element, index) : frames, { duration, delay, easing, fill: 'both' });
          animation.startTime = start; this.animations.push(animation); return animation;
        });
      };
      // One continuous lift and tear, followed by two extra turns before the reveal.
      run('.pack-opening-wrapper', [
        { transform: this.initialPose, offset: 0 },
        { transform: 'translate3d(0,-10px,0) rotate(-7deg) rotateY(-9deg) scale(1.035)', offset: .55 },
        { transform: 'translate3d(0,-14px,0) rotate(2deg) rotateY(0deg) scale(1.02)' }
      ], 690);
      run('.pack-opening-seam', [
        { transform: 'scaleX(0)', opacity: 0, offset: 0 },
        { transform: 'scaleX(.2)', opacity: .85, offset: .2 },
        { transform: 'scaleX(1)', opacity: 1, offset: .78 },
        { transform: 'scaleX(1)', opacity: 0 }
      ], 510, 180, 'linear');
      run('.pack-opening-foil--1', [
        { transform: 'translate3d(0,0,0) rotate(0deg)', opacity: 1, offset: 0 },
        { transform: 'translate3d(-16%,-14%,30px) rotate(-16deg) rotateX(12deg)', opacity: 1, offset: .48 },
        { transform: 'translate3d(-55%,-58%,60px) rotate(-38deg) rotateX(35deg)', opacity: 0 }
      ], 950, 490, 'cubic-bezier(.2,.7,.2,1)');
      run('.pack-opening-foil--2', [
        { transform: 'translate3d(0,0,0) rotate(0deg)', opacity: 1, offset: 0 },
        { transform: 'translate3d(3%,28%,30px) rotate(5deg) rotateX(8deg)', opacity: 1, offset: .42 },
        { transform: 'translate3d(15%,110%,70px) rotate(18deg) rotateX(32deg)', opacity: 0 }
      ], 1080, 850, 'cubic-bezier(.45,0,.25,1)');
      run('.pack-opening-flight', [
        { visibility: 'visible', transform: 'translate3d(0,18%,0) rotate(-6deg) scale(.84)', offset: 0, easing: smooth },
        { visibility: 'visible', transform: 'translate3d(0,-13%,0) rotate(-3deg) scale(1.045)', offset: .28, easing: 'ease-in-out' },
        { visibility: 'visible', transform: 'translate3d(0,-7%,0) rotate(-1deg) scale(1.035)', offset: .54, easing: 'ease-in-out' },
        { visibility: 'visible', transform: `translate3d(0,-6%,0) rotate(0deg) scale(${highlight ? 1.14 : 1.085})`, offset: .7, easing: smooth },
        { visibility: 'visible', transform: 'translate3d(0,-4%,0) rotate(0deg) scale(1)' }
      ], 3980, 820, 'linear');
      run('.pack-opening-card', [
        { transform: 'rotateY(180deg)', offset: 0, easing: 'cubic-bezier(.2,.1,.35,1)' },
        { transform: 'rotateY(-540deg)', offset: .52 },
        { transform: 'rotateY(-540deg)', offset: .6, easing: 'cubic-bezier(.42,0,.13,1)' },
        { transform: 'rotateY(-720deg)', offset: 1 }
      ], 2500, 1500, 'linear');
      // The light wipes away the cover during the final turn, revealing the
      // already decoded photograph behind it. Both earlier turns stay covered.
      const revealStart = 3260, revealDuration = 680;
      const [cue] = run('.pack-opening-cover', [
        { clipPath: 'inset(0 0 0 0%)' },
        { clipPath: 'inset(0 0 0 100%)' }
      ], revealDuration, revealStart, 'linear');
      cue.finished.then(() => { if (generation === this.generation && this.pending) { this.dataset.faceRevealed = ''; this.emit('reveal'); } }).catch(() => {});
      run('.pack-opening-shine', [
        { transform: 'translateX(0%) skewX(-12deg)', opacity: 0, offset: 0 },
        { transform: 'translateX(13.514%) skewX(-12deg)', opacity: 1, offset: .1 },
        { transform: 'translateX(87.838%) skewX(-12deg)', opacity: 1, offset: .65 },
        { transform: 'translateX(135.135%) skewX(-12deg)', opacity: 0 }
      ], revealDuration, revealStart, 'linear');
      run('.pack-opening-beam', [
        { opacity: 0, transform: 'scale(.35,.75)', offset: 0 },
        { opacity: .72, transform: 'scale(1)', offset: .36 },
        { opacity: 0, transform: 'scale(1.2,1.08)' }
      ], 1800, 480, 'ease-in-out');
      run('.pack-opening-aura', [
        { opacity: .2, transform: 'scale(.88)', offset: 0 },
        { opacity: .7, transform: 'scale(1.12)', offset: .27 },
        { opacity: .2, transform: 'scale(.75)', offset: .46 },
        { opacity: 1, transform: 'scale(1.28)', offset: .68 },
        { opacity: .7, transform: 'scale(1.1)' }
      ], total, 0, 'ease-in-out');
      run('.pack-opening-halo', [
        { opacity: 0, transform: 'translateY(-4%) scale(.35)', offset: 0 },
        { opacity: .45, transform: 'translateY(-4%) scale(.7)', offset: .2 },
        { opacity: 1, transform: `translateY(-4%) scale(${highlight ? 1.38 : 1.15})`, offset: .38 },
        { opacity: .8, transform: 'translateY(-4%) scale(1)' }
      ], 2400, 2800, 'ease-out');
      run('.pack-opening-card-glow', [
        { opacity: 0, transform: 'translateY(-4%) scale(.94)', offset: 0 },
        { opacity: 1, transform: 'translateY(-4%) scale(1.035)', offset: .26 },
        { opacity: .8, transform: 'translateY(-4%) scale(1.015)' }
      ], 2000, 3200, 'ease-out');
      const wave = [
        { opacity: 0, transform: 'scale(.2)', offset: 0 },
        { opacity: .95, transform: 'scale(.58)', offset: .14 },
        { opacity: .5, transform: 'scale(1.05)', offset: .55 },
        { opacity: 0, transform: 'scale(1.55)' }
      ];
      run('.pack-opening-ring:not(.pack-opening-ring--echo)', wave, 1400, 3300, 'cubic-bezier(.12,.65,.3,1)');
      run('.pack-opening-ring--echo', wave, 1400, 3530, 'cubic-bezier(.12,.65,.3,1)');
      run('.pack-opening-flare', [
        { opacity: 0, transform: 'scaleX(.1)', offset: 0 },
        { opacity: 1, transform: 'scaleX(1)', offset: .22 },
        { opacity: 0, transform: 'scaleX(1.2)' }
      ], 950, 3260, 'ease-out');
      run('.pack-opening-rays', [
        { opacity: 0, transform: 'scale(.35) rotate(-20deg)', offset: 0 },
        { opacity: 1, transform: 'scale(1.08) rotate(-9deg)', offset: .3 },
        { opacity: highlight ? .85 : .5, transform: 'scale(1) rotate(0deg)' }
      ], 2050, 3150, 'ease-out');
      run('.pack-opening-dust', [{ opacity: 0 }, { opacity: .75 }], 1400, 3800, 'ease-out');
      run('.pack-opening-spark', (_element, index) => {
        const angle = (index * 137.508 + 12) * Math.PI / 180;
        const distance = (185 + (index % 7) * 22) * (highlight ? 1.2 : 1);
        const x = Math.cos(angle) * distance, y = Math.sin(angle) * distance * 1.15;
        return [
          { opacity: 0, transform: 'translate3d(0,0,0) scale(.2)', offset: 0 },
          { opacity: 1, transform: `translate3d(${x * .62}px,${y * .62}px,0) scale(1.3)`, offset: .18 },
          { opacity: .7, transform: `translate3d(${x * .85}px,${y * .85}px,0) scale(.9)`, offset: .52 },
          { opacity: 0, transform: `translate3d(${x}px,${y + 25}px,0) scale(.25)` }
        ];
      }, 1900, 3240, 'cubic-bezier(.12,.55,.3,1)');
      run('.pack-opening-floor', [{ opacity: .6, transform: 'scale(1)' }, { opacity: .25, transform: 'scale(.65)' }], 2300);
      run('.pack-opening-back-shine', [{ transform: 'translateX(-110%) rotate(15deg)' }, { transform: 'translateX(110%) rotate(15deg)' }], 630, 1210, 'ease-in-out');
      const [clock] = run('', [{ '--opening-clock': 0 }, { '--opening-clock': 1 }], total, 0, 'linear');
      clock.finished.then(() => this.finish()).catch(() => {});
    }

    finish() {
      const wasComplete = this.dataset.state === 'complete';
      const hadFace = this.hasAttribute('data-face-revealed');
      ++this.generation;
      this.sound?.stop();
      this.dataset.state = 'complete'; this.dataset.faceRevealed = '';
      this.animations?.forEach(animation => animation.cancel()); this.animations = [];
      this.querySelector('[data-opening-trigger]')?.setAttribute('disabled', '');
      const resolve = this.resolve;
      this.pending = null; this.resolve = null; this.reject = null;
      if (!hadFace) this.emit('reveal');
      if (!wasComplete) this.emit('complete');
      resolve?.();
    }

    reset() {
      if (this.pending) this.finish();
      ++this.generation;
      this.dataset.state = 'sealed'; delete this.dataset.faceRevealed;
      this.querySelector('[data-opening-trigger]')?.removeAttribute('disabled');
    }
  }
  customElements.define('piranha-pack-opening', PiranhaPackOpening);
})();
